import { generatePath } from 'react-router-dom';
import { API_ROUTES } from '../consts';
import PaginationResponseDto from '../interfaces/paginationResponse.interface';
import {
  AccountReferrals, Referral, ReferralForceSendResult, ReferralsQuery,
} from '../@types/Referral';
import request from './index';

type ReferralsRequestParams = {
  page: number,
  limit: number,
  status?: string,
  eligibility?: string,
  unlinked?: boolean,
  payoutFailed?: boolean,
  noHubspotStatus?: boolean,
};

// Send only the filters that are on, so the gateway sees "not set" rather than "false".
export const buildReferralsRequestParams = (query: ReferralsQuery): ReferralsRequestParams => ({
  page: query.page,
  limit: query.limit,
  ...(query.status && { status: query.status }),
  ...(query.eligibility && { eligibility: query.eligibility }),
  ...(query.unlinked && { unlinked: true }),
  ...(query.payoutFailed && { payoutFailed: true }),
  ...(query.noHubspotStatus && { noHubspotStatus: true }),
});

export const getReferrals = async (query: ReferralsQuery): Promise<PaginationResponseDto<Referral>> => {
  const { data } = await request.get(API_ROUTES.REFERRALS, {
    params: buildReferralsRequestParams(query),
  });
  const {
    results, page, totalCount, limit,
  } = data;

  return {
    results: results || [],
    page,
    totalCount: totalCount || 0,
    limit,
  };
};

export const getAccountReferrals = async (accountId: string): Promise<AccountReferrals> => {
  const path = generatePath(API_ROUTES.ACCOUNT_REFERRALS, { accountId });
  const { data } = await request.get(path);

  return data;
};

export const overrideReferralEligibility = async (referralId: string, reason: string): Promise<void> => {
  const path = generatePath(API_ROUTES.REFERRAL_ELIGIBILITY, { referralId });

  await request.patch(path, { reason });
};

export const recheckReferralEligibility = async (referralId: string): Promise<void> => {
  const path = generatePath(API_ROUTES.REFERRAL_RECHECK, { referralId });

  await request.post(path);
};

export const updateReferredAccount = async (referralId: string, accountId: string | null): Promise<void> => {
  const path = generatePath(API_ROUTES.REFERRAL_REFERRED_ACCOUNT, { referralId });

  await request.put(path, { accountId });
};

export const updateReferralPayingOutsideStripe = async (
  referralId: string,
  payingOutsideStripe: boolean,
): Promise<void> => {
  const path = generatePath(API_ROUTES.REFERRAL, { referralId });

  await request.patch(path, { payingOutsideStripe });
};

export const qualifyReferral = async (referralId: string): Promise<void> => {
  const path = generatePath(API_ROUTES.REFERRAL_QUALIFY, { referralId });

  await request.post(path);
};

export const resendReferralReward = async (referralRewardId: string, newEmail?: string): Promise<void> => {
  const path = generatePath(API_ROUTES.REFERRAL_REWARD_RESEND, { referralRewardId });

  await request.post(path, newEmail ? { newEmail } : {});
};

export const forceSendReferralReward = async (referralRewardId: string): Promise<ReferralForceSendResult> => {
  const path = generatePath(API_ROUTES.REFERRAL_REWARD_FORCE_SEND, { referralRewardId });
  const { data } = await request.post(path);

  return data;
};

export const endReferralPosition = async (referralPositionId: string): Promise<void> => {
  const path = generatePath(API_ROUTES.REFERRAL_POSITION_END, { referralPositionId });

  await request.post(path);
};

export const downloadReferralTaxReportCsv = async (year: number): Promise<Blob> => {
  const { data } = await request.get(API_ROUTES.REFERRAL_TAX_REPORT_CSV, {
    params: { year },
    responseType: 'blob',
  });

  return data;
};
